import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  FiUsers,
  FiShield,
  FiUserX,
  FiUserCheck,
  FiTrash2,
  FiDollarSign,
  FiActivity,
} from 'react-icons/fi';

/**
 * Admin - דף ניהול משתמשים
 * פאנל ניהול מערכת המאפשר צפייה במשתמשים, שינוי סטטוס ומחיקה
 */
const Admin = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * עיצוב מספר למטבע ישראלי
   * @param {number} value - הערך המספרי
   * @returns {string} מחרוזת מעוצבת עם סימן שקל
   */
  const formatCurrency = (value) => {
    if (value == null) return '₪0';
    return `₪${Number(value).toLocaleString('he-IL')}`;
  };

  /**
   * טעינת רשימת המשתמשים מהשרת
   */
  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('שגיאה בטעינת משתמשים:', err);
      setError('שגיאה בטעינת רשימת המשתמשים');
    }
  };

  /**
   * טעינת סטטיסטיקות מערכת מהשרת
   */
  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('שגיאה בטעינת סטטיסטיקות:', err);
    }
  };

  /**
   * טעינה ראשונית של נתונים
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  /**
   * החלפת סטטוס פעיל/מושבת של משתמש
   * @param {number} userId - מזהה המשתמש
   */
  const handleToggleActive = async (userId) => {
    try {
      await adminAPI.toggleActive(userId);
      await fetchUsers();
    } catch (err) {
      console.error('שגיאה בשינוי סטטוס משתמש:', err);
    }
  };

  /**
   * החלפת תפקיד מנהל/משתמש
   * @param {number} userId - מזהה המשתמש
   */
  const handleToggleAdmin = async (userId) => {
    try {
      await adminAPI.toggleAdmin(userId);
      await fetchUsers();
    } catch (err) {
      console.error('שגיאה בשינוי תפקיד משתמש:', err);
    }
  };

  /**
   * מחיקת משתמש עם אישור
   * @param {number} userId - מזהה המשתמש
   * @param {string} username - שם המשתמש להצגה בהודעת האישור
   */
  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`האם למחוק את המשתמש ${username}?`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      await fetchUsers();
    } catch (err) {
      console.error('שגיאה במחיקת משתמש:', err);
    }
  };

  /**
   * תצוגת טעינה - ספינר
   */
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* כותרת הדף */}
      <div className="page-header">
        <h1>ניהול משתמשים</h1>
        <p className="page-subtitle">פאנל ניהול מערכת</p>
      </div>

      {/* הודעת שגיאה */}
      {error && <div className="alert alert-error">{error}</div>}

      {/* רשת כרטיסי סיכום */}
      <div className="summary-grid">
        {/* סה"כ משתמשים */}
        <div className="summary-card">
          <div className="summary-card-icon" style={{ color: '#2563eb' }}>
            <FiUsers />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">סה"כ משתמשים</span>
            <span className="summary-card-value">
              {stats.total_users || 0}
            </span>
          </div>
        </div>

        {/* משתמשים פעילים */}
        <div className="summary-card">
          <div className="summary-card-icon" style={{ color: '#059669' }}>
            <FiActivity />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">משתמשים פעילים</span>
            <span className="summary-card-value">
              {stats.active_users || 0}
            </span>
          </div>
        </div>

        {/* סה"כ הכנסות במערכת */}
        <div className="summary-card">
          <div className="summary-card-icon" style={{ color: '#16a34a' }}>
            <FiDollarSign />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">סה"כ הכנסות במערכת</span>
            <span className="summary-card-value">
              {formatCurrency(stats.total_incomes)}
            </span>
          </div>
        </div>

        {/* סה"כ הוצאות במערכת */}
        <div className="summary-card">
          <div className="summary-card-icon" style={{ color: '#dc2626' }}>
            <FiDollarSign />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">סה"כ הוצאות במערכת</span>
            <span className="summary-card-value">
              {formatCurrency(stats.total_expenses)}
            </span>
          </div>
        </div>

        {/* סה"כ מעשר ששולם */}
        <div className="summary-card">
          <div className="summary-card-icon" style={{ color: '#d97706' }}>
            <FiDollarSign />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">סה"כ מעשר ששולם</span>
            <span className="summary-card-value">
              {formatCurrency(stats.total_maaser_paid)}
            </span>
          </div>
        </div>
      </div>

      {/* כרטיס טבלת משתמשים */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">רשימת משתמשים</h3>
        </div>

        {/* תצוגת שולחן - מחשב בלבד */}
        <div className="desktop-only">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>שם מלא</th>
                  <th>שם משתמש</th>
                  <th>אימייל</th>
                  <th>סטטוס</th>
                  <th>תפקיד</th>
                  <th>הכנסות</th>
                  <th>הוצאות</th>
                  <th>מעשר ששולם</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      {user.is_active_user ? (
                        <span className="badge badge-success">פעיל</span>
                      ) : (
                        <span className="badge badge-danger">מושבת</span>
                      )}
                    </td>
                    <td>
                      {user.is_admin ? (
                        <span className="badge badge-warning">מנהל</span>
                      ) : (
                        <span className="badge badge-info">משתמש</span>
                      )}
                    </td>
                    <td>{formatCurrency(user.total_incomes)}</td>
                    <td>{formatCurrency(user.total_expenses)}</td>
                    <td>{formatCurrency(user.total_maaser_paid)}</td>
                    <td>
                      <div className="actions">
                        {/* כפתור החלפת סטטוס פעיל/מושבת */}
                        <button
                          className="btn btn-sm btn-icon btn-outline"
                          onClick={() => handleToggleActive(user.id)}
                          title={user.is_active_user ? 'השבת משתמש' : 'הפעל משתמש'}
                        >
                          {user.is_active_user ? <FiUserX /> : <FiUserCheck />}
                        </button>

                        {/* כפתור החלפת תפקיד מנהל/משתמש */}
                        <button
                          className="btn btn-sm btn-icon btn-outline"
                          onClick={() => handleToggleAdmin(user.id)}
                          title={user.is_admin ? 'הסר הרשאת מנהל' : 'הפוך למנהל'}
                        >
                          <FiShield />
                        </button>

                        {/* כפתור מחיקת משתמש */}
                        <button
                          className="btn btn-danger btn-sm btn-icon"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          title="מחק משתמש"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* תצוגת כרטיסים - מובייל בלבד */}
        <div className="mobile-only">
          <div className="mobile-list">
            {users.map((user) => (
              <div className="admin-user-card" key={user.id}>
                <div className="admin-user-card-header">
                  <div className="admin-user-card-avatar">
                    {user.first_name ? user.first_name.charAt(0) : '?'}
                  </div>
                  <div>
                    <div className="admin-user-card-name">{user.first_name} {user.last_name}</div>
                    <div className="admin-user-card-email">{user.email}</div>
                  </div>
                </div>
                <div className="admin-user-card-badges">
                  {user.is_active_user ? (
                    <span className="badge badge-success">פעיל</span>
                  ) : (
                    <span className="badge badge-danger">מושבת</span>
                  )}
                  {user.is_admin ? (
                    <span className="badge badge-warning">מנהל</span>
                  ) : (
                    <span className="badge badge-info">משתמש</span>
                  )}
                </div>
                <div className="admin-user-card-stats">
                  <div className="admin-stat-item">
                    <span className="admin-stat-label">הכנסות</span>
                    <span className="admin-stat-value">{formatCurrency(user.total_incomes)}</span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-label">הוצאות</span>
                    <span className="admin-stat-value">{formatCurrency(user.total_expenses)}</span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-label">מעשר</span>
                    <span className="admin-stat-value">{formatCurrency(user.total_maaser_paid)}</span>
                  </div>
                </div>
                <div className="admin-user-card-actions">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleToggleActive(user.id)}
                    title={user.is_active_user ? 'השבת' : 'הפעל'}
                  >
                    {user.is_active_user ? <FiUserX /> : <FiUserCheck />}
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleToggleAdmin(user.id)}
                    title={user.is_admin ? 'הסר מנהל' : 'הפוך למנהל'}
                  >
                    <FiShield />
                  </button>
                  <button
                    className="btn btn-danger btn-sm btn-icon"
                    onClick={() => handleDeleteUser(user.id, user.username)}
                    title="מחק"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* מצב ריק - אין משתמשים */}
        {users.length === 0 && (
          <div className="empty-state">
            <p>לא נמצאו משתמשים במערכת</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
